import { useEffect, useState } from "react";
import { isNativeApp } from "./native.ts";

/** Chrome/Edge'in kurulum istemini yakalar; iOS'ta bu olay hiç gelmez. */
interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferred: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function announce() {
  for (const listener of listeners) listener();
}

window.addEventListener("beforeinstallprompt", (event) => {
  event.preventDefault();
  deferred = event as InstallPromptEvent;
  announce();
});

window.addEventListener("appinstalled", () => {
  deferred = null;
  announce();
});

/** Uygulama ana ekrandan (tam ekran) mı açıldı? */
export function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS Safari standart API'yi desteklemiyor, kendi bayrağını kullanıyor.
    (navigator as { standalone?: boolean }).standalone === true
  );
}

export function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export type InstallState = "unavailable" | "prompt" | "ios-manual";

export function useInstallState(): InstallState {
  const [available, setAvailable] = useState(() => deferred !== null);

  useEffect(() => {
    const listener = () => setAvailable(deferred !== null);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  if (isNativeApp() || isStandalone()) return "unavailable";
  if (available) return "prompt";
  if (isIos()) return "ios-manual";
  return "unavailable";
}

export async function promptInstall(): Promise<boolean> {
  if (!deferred) return false;
  await deferred.prompt();
  const choice = await deferred.userChoice;
  deferred = null;
  announce();
  return choice.outcome === "accepted";
}

/** Ağ durumu — düello ve günlük kelime bağlantı ister. */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);
  return online;
}
